import React, { useState, useEffect, useContext } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ThemeContext from "../context/ThemeContext";
import DocumentContext from "../context/DocumentContext";
import ArchiveContext from "../context/ArchiveContext";
import AuthContext from "../context/AuthContext";
import LanguageContext from "../context/LanguageContext";
import { getLocalizedTitle, getLocalizedDescription, getLocalizedAuthor, getTextDirection } from "../utils/languageUtils";
import AddDocumentDialog from './AddDocumentDialog';
import ReadMore from './ReadMore';

export default function DocumentsList() {
    const { authorId } = useParams();
    const { theme } = useContext(ThemeContext);
    const { language } = useContext(LanguageContext);
    const { getAllDocuments, documents, deleteDocument } = useContext(DocumentContext);
    const { archiveItems, loading: archiveLoading } = useContext(ArchiveContext);
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ show: false, documentId: null, documentName: '' });
    const navigate = useNavigate();
    
    // Get the archive item details
    const archiveItem = archiveItems?.find(item => item.id === parseInt(authorId));
    
    useEffect(() => {
        let isMounted = true;
        
        const fetchData = async () => {
            if (!authorId) return;
            
            try {
                if (isMounted) setLoading(true);
                await getAllDocuments(authorId);
            } catch (err) {
                if (isMounted) setError(err.message);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        
        fetchData();
        
        return () => {
            isMounted = false;
        };
    }, [authorId]); // Only depend on authorId
    
    const handleAddDocumentClick = () => {
        setShowDialog(true);
    };

    const handleDialogClose = () => {
        setShowDialog(false);
    };

    const handleDeleteClick = (documentId, documentName) => {
        setDeleteDialog({ show: true, documentId, documentName });
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteDocument(deleteDialog.documentId);
            // Refresh the documents list after deletion
            await getAllDocuments(authorId);
        } catch (error) {
            console.error('Error deleting document:', error);
        } finally {
            setDeleteDialog({ show: false, documentId: null, documentName: '' });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialog({ show: false, documentId: null, documentName: '' });
    };
    
    if (loading) {
        return (
            <div className="documents-list">
                <div className="documents-list__loading">
                    <p>Loading documents...</p>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="documents-list">
                <div className="documents-list__error">
                    <h2>Error Loading Documents</h2>
                    <p>{error}</p>
                    <Link to="/archive" className="documents-list__back-button">
                        Return to Archive
                    </Link>
                </div>
            </div>
        );
    }
    
    if (archiveLoading) {
        return (
            <div className="documents-list">
                <div className="documents-list__loading">
                    <p>Loading archive...</p>
                </div>
            </div>
        );
    }
    
    if (!archiveItem) {
        return (
            <div className="documents-list">
                <div className="documents-list__error">
                    <h2>Archive Item Not Found</h2>
                    <p>The requested archive item could not be found.</p>
                    <Link to="/archive" className="documents-list__back-button">
                        Return to Archive
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="documents-list">
            {deleteDialog.show && (
                <>
                    <div className="delete-dialog__overlay" onClick={handleDeleteCancel} />
                    <div className="delete-dialog">
                        <h2 className="delete-dialog__title">Delete Document</h2>
                        <p className="delete-dialog__message">
                            Are you sure you want to permanently delete "{deleteDialog.documentName}"? 
                            This action cannot be undone.
                        </p>
                        <div className="delete-dialog__buttons">
                            <button 
                                className="delete-dialog__button delete-dialog__button--cancel"
                                onClick={handleDeleteCancel}
                            >
                                Cancel
                            </button>
                            <button 
                                className="delete-dialog__button delete-dialog__button--delete"
                                onClick={handleDeleteConfirm}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div className="documents-list__header">
                <button 
                    onClick={() => navigate('/archive')} 
                    className="back-button"
                >
                    &larr; Back to Archive
                </button>
                
                <div className="documents-list__author-info">
                    <div className="documents-list__author-image-container">
                        <img 
                            src={archiveItem.image_url} 
                            alt={getLocalizedTitle(archiveItem.title, archiveItem.persian_title, language)}
                            className="documents-list__author-image"
                            onError={(e) => {
                                e.target.src = "/images/placeholder.jpg";
                            }}
                        />
                    </div>
                    
                    <div className="documents-list__author-bio">
                        <h1 
                            className="documents-list__title"
                            dir={getTextDirection(language)}
                        >
                            {getLocalizedTitle(archiveItem.title, archiveItem.persian_title, language)}
                        </h1>
                        <p className="documents-list__author-period">
                            {archiveItem.start_year && archiveItem.end_year 
                                ? `${archiveItem.start_year} - ${archiveItem.end_year}`
                                : archiveItem.start_year || archiveItem.end_year || ''
                            }
                        </p>
                        <ReadMore 
                            text={getLocalizedDescription(archiveItem.description, archiveItem.persian_description, language)}
                            maxHeight="4.8em"
                            dir={getTextDirection(language)}
                        />
                    </div>
                </div>
            </div>
            
            {user && (
                <div className="documents-list__admin-section">
                    <button 
                        className="documents-list__add-button"
                        onClick={() => setShowDialog(true)}
                    >
                        <i className="fas fa-plus"></i>
                        Add New Document
                    </button>
                </div>
            )}
            
            {showDialog && (
                <AddDocumentDialog
                    onClose={handleDialogClose}
                    archiveItemId={authorId}
                />
            )}
            
            {documents && documents.length > 0 ? (
                <>
                    <div className="documents-list__count">
                        <p>{documents.length} {documents.length === 1 ? 'document' : 'documents'} available</p>
                    </div>
                    <div className="documents-list__grid">
                        {documents.map(document => (
                            <div key={document.id} className="document-item">
                                <div className="document-item__image-container">
                                    <img 
                                        src={document.image_url || "/images/placeholder.jpg"} 
                                        alt={getLocalizedTitle(document.name, document.persian_name, language)}
                                        className="document-item__image"
                                        onError={(e) => {
                                            e.target.src = "/images/placeholder.jpg";
                                        }}
                                    />
                                </div>
                                <div className="document-item__content">
                                    <div className="document-item__header">
                                        <h2 
                                            className="document-item__title"
                                            dir={getTextDirection(language)}
                                        >
                                            {getLocalizedTitle(document.name, document.persian_name, language)}
                                        </h2>
                                        {user && (
                                            <button 
                                                className="document-item__delete"
                                                onClick={() => handleDeleteClick(document.id, getLocalizedTitle(document.name, document.persian_name, language))}
                                                aria-label="Delete document"
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                    <p className="document-item__year">{document.year}</p>
                                    <ReadMore 
                                        text={getLocalizedDescription(document.description, document.persian_description, language)}
                                        maxHeight="4.8em"
                                        dir={getTextDirection(language)}
                                    />
                                    <p 
                                        className="document-item__author"
                                        dir={getTextDirection(language)}
                                    >
                                        {getLocalizedAuthor(document.author, document.persian_author, language)}
                                    </p>
                                    <div className="document-item__footer">
                                        <span className="document-item__pages">
                                            {document.pages_count || 0} pages
                                        </span>
                                        <Link 
                                            to={`/document/${document.id}`} 
                                            state={{ document: document }}
                                            className="document-item__button"
                                        >
                                            View Document
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="documents-list__empty">
                    <div className="documents-list__empty-content">
                        <h2>No Documents Available</h2>
                        <p>There are currently no documents under this archive item.</p>
                        <Link to="/archive" className="documents-list__back-button">
                            Return to Archive
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
} 